import { getScopedPurchaserId } from '@openeducation/hr-js-utils';
import {
  useAdminModeAuth,
  useCognitoAuth,
  usePrimaryPurchaserId,
} from '@openeducation/hr-rn-cognito-providers';
import {
  useHRPageTemplateProps,
  useOnLogout,
  useOmLanguages,
  CenteredLoading,
  flex,
  getLanguageByLocalization,
  useLogoutWhenIdle,
} from '@openeducation/hr-rn-components';
import { useAuthorizedPurchaserIds } from '@openeducation/hr-rn-graphql-providers';
import {
  PurchaserScopeProvider,
  useHRGetLocalLanguage,
  useIsPurchaserScopeReady,
} from '@openeducation/hr-rn-providers';
import * as Localization from 'expo-localization';
import React from 'react';

import { PageTemplate } from './page-template';
import { getOrganizationByPath, MULTIPLE_PURCHASER_PATHS, SINGLE_PURCHASER_PATHS } from './paths';
import { RouteType } from './private-routes';
import { publicRoutes } from './public-routes';
import { Route, Routes, useLocation } from './router';
import {
  useExternalPortalGuard,
  useNavItems,
  useResolveInitialRoute,
  useSetHRPortalConfig,
  usePrivateRoutes,
} from '../hooks';

const Loader = () => <CenteredLoading size={100} style={[flex('flex-1')]} />;

const AppPublicRoutes = () => {
  const locale = Localization.locale;
  const { isLocalLanguageLoading } = useHRGetLocalLanguage(getLanguageByLocalization, locale);

  if (isLocalLanguageLoading) {
    return <Loader />;
  }

  return (
    <Routes>
      {publicRoutes.map((route) => (
        <Route key={route.path} path={route.path} element={route.component} />
      ))}
    </Routes>
  );
};

const AppPrivateRoutes = () => {
  const navItems = useNavItems();
  const pageTemplateProps = useHRPageTemplateProps();
  const { routes, showFullPageLoader } = usePrivateRoutes();
  const isPurchaserScopeReady = useIsPurchaserScopeReady();

  useExternalPortalGuard();
  useOmLanguages();

  if (showFullPageLoader) {
    return <Loader />;
  }

  return (
    <PageTemplate {...pageTemplateProps} navItems={navItems}>
      {isPurchaserScopeReady ? (
        <Routes>
          {routes.map((route: RouteType) => (
            <Route key={route.path} path={route.path} element={route.component} />
          ))}
        </Routes>
      ) : (
        <Loader />
      )}
    </PageTemplate>
  );
};

const AppRoutes = () => {
  const { pathname, search } = useLocation();
  const checkAdmin = pathname === '/login' || pathname === '/';
  useAdminModeAuth(checkAdmin);

  const { isLoggedIn, logout } = useCognitoAuth(true);
  useResolveInitialRoute(isLoggedIn);
  useLogoutWhenIdle(isLoggedIn, logout);
  useSetHRPortalConfig();
  const organization = getOrganizationByPath(pathname);
  const authorizedPurchaserIds = useAuthorizedPurchaserIds({ organization });
  const primaryPurchaserId = usePrimaryPurchaserId();
  const scopedPurchaserId = getScopedPurchaserId(pathname, search, SINGLE_PURCHASER_PATHS);
  useOnLogout(isLoggedIn);

  if (isLoggedIn === null) {
    return <Loader />;
  }

  if (!isLoggedIn) {
    return <AppPublicRoutes />;
  }

  return (
    <PurchaserScopeProvider
      allowMultiple={MULTIPLE_PURCHASER_PATHS.includes(pathname)}
      authorizedPurchaserIds={authorizedPurchaserIds}
      organization={organization}
      primaryPurchaserId={primaryPurchaserId}
      scopedPurchaserId={scopedPurchaserId}
    >
      <AppPrivateRoutes />
    </PurchaserScopeProvider>
  );
};

export default AppRoutes;
